import React, {
    lazy,
    Suspense,
    useState,
    useEffect,
    useRef
} from 'react';

import './Home.css';

import logoHomepage from '../../assets/img/logo-homepage.png';
import logoHomepageMobile from '../../assets/img/logoMobile.png';

import mobileVideo from '../../assets/video/mobile-720.webm';
import desktopVideo from '../../assets/video/desktop.webm';

import { ArrowDown } from 'lucide-react';
import { useIsMobile } from '../../lib/Utilis.js';

function Home() {
    const [isHomeContentVisible, setIsHomeContentVisible] = useState(false);

    const contentRef = useRef(null);

    const Collection = lazy(() =>
        import('../Catalog/Collection/Collection.js')
    );

    const isMobile = useIsMobile();

    /*
     * Lazy-load Collection.js lorsque Home Content
     * commence à entrer dans le viewport.
     */
    useEffect(() => {
        const currentRef = contentRef.current;

        if (!currentRef) {
            return;
        }

        /*
         * .home est le conteneur qui fait réellement le scroll.
         */
        const scrollContainer = currentRef.closest('.home');

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsHomeContentVisible(true);

                    /*
                     * Une fois Collection.js déclenché,
                     * on n'a plus besoin de surveiller.
                     */
                    observer.disconnect();
                }
            },
            {
                root: scrollContainer,
                threshold: 0.1
            }
        );

        observer.observe(currentRef);

        return () => {
            observer.disconnect();
        };
    }, []);

    /*
     * Scroll vers le début de Home Content.
     */
    const handleScroll = () => {
        const contentSection = contentRef.current;

        if (!contentSection) {
            return;
        }

        contentSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    };

    return (
        <div className="home">

            {/* =========================
                HOME HEADER
                ========================= */}

            <div className="home-header">

                <video
                    className="home-header-video"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    poster={
                        isMobile
                            ? logoHomepageMobile
                            : logoHomepage
                    }
                    aria-hidden="true"
                >
                    {/* Mobile */}
                    <source
                        src={mobileVideo}
                        type="video/webm"
                        media="(max-width: 600px)"
                    />

                    {/* Desktop */}
                    <source
                        src={desktopVideo}
                        type="video/webm"
                        media="(min-width: 601px)"
                    />
                </video>

                <div
                    className="home-header-overlay"
                    aria-hidden="true"
                />

                <div className="home-header-content">

                    <div className="home-title">

                        <img
                            src={
                                isMobile
                                    ? logoHomepageMobile
                                    : logoHomepage
                            }
                            alt="Logo Delisle Couture"
                            className="home-logo"
                        />

                        <p>
                            S'affirmer, c'est le porter.
                        </p>

                    </div>

                    <div className="home-subtitle">

                        <button
                            type="button"
                            className="home-scroll-button"
                            onClick={handleScroll}
                            aria-label="Faire défiler vers les collections"
                        >
                            <ArrowDown
                                size={isMobile ? 64 : 128}
                                className="home-clickable"
                                aria-hidden="true"
                            />
                        </button>

                    </div>

                </div>
            </div>

            {/* =========================
                HOME CONTENT
                ========================= */}

            <div
                className="home-content"
                ref={contentRef}
            >
                <div
                    className={`home-content-wrapper ${
                        isHomeContentVisible
                            ? 'visible'
                            : ''
                    }`}
                >
                    <Suspense
                        fallback={
                            <div>Loading...</div>
                        }
                    >
                        <Collection
                            isVisible={
                                isHomeContentVisible
                            }
                        />
                    </Suspense>
                </div>
            </div>

        </div>
    );
}

export default Home;